<template>
  <div>
    <div class="container-fluid">
      <div class="mt-4">
        <AlertBannerRecords path="/vehicles" :message="vehiclesMessage" />
        <AlertBannerRecords path="/maestro/tickets" :message="scheduleMessage" />
        <FutureAlert :show-count="false" :data="futureAlertData" :loading="futureAlertData.loading" />
      </div>

      <div class="filters-card">
        <div class="row">
          <div class="col-md-12 mb-3">
            <h5 class="mb-0">
              <filter-icon size="1.2x" class="mr-2" />
              <strong>Filtros</strong>
            </h5>
          </div>
        </div>

        <div class="row">
          <div class="col-md-2">
            <label class="mr-2" for="period-type">
              Tipo de período
              <info-icon
                  size="1x"
                  v-tooltip.top="'Alguns órgãos podem não fornecer a data da infração e estas multas não serão incluídas nos gráficos ao pesquisar por este tipo de período. ' +
                   'Para garantir a visualização de todas as multas, acesse o aplicativo e faça uma busca pelo status da multa'"
              />
            </label>
            <select v-model="periodType" id="period-type" class="form-control">
              <option value="occurred_at">Data da infração</option>
              <option value="expired_at">Data de vencimento</option>
              <option value="updated_at">Data de atualização</option>
              <option value="created_at">Data de criação</option>
            </select>
          </div>

          <div class="col-md-2">
            <label for="start-date">Data inicial</label>
            <input v-model="startDate" type="date" id="start-date" class="form-control">
          </div>

          <div class="col-md-2">
            <label for="end-date">Data final</label>
            <input v-model="endDate" type="date" id="end-date" class="form-control">
          </div>

          <div class="col-md-2">
            <label for="entity-status">Status do Veículo</label>
            <select v-model="entityStatusId" id="entity-status" class="form-control">
              <option v-for="[label, value] in entityStatuses" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>

          <div class="col-md-4 filters-card-actions">
            <button @click="initializeData" class="btn btn-primary" :disabled="isLoadingData">
              {{ isLoadingData ? 'Carregando...' : 'Aplicar Filtros' }}
            </button>
            <router-link
                tag="button"
                :to="appLink"
                class="btn btn-primary"
            >
              Acessar aplicativo
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <!-- Status -->
      <div class="row">
        <div class="col-md-3" v-for="(status, index) in listStatus" :key="status.title || index">
          <Card :data="status" :loading="objectLoad.status" data-sum :link="status.link"/>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <cm-frotas-pie-chart graph-title="Classificação" :classifications="classifications" :loading="objectLoad.graph"/>
          </div>
        </div>

        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <cm-frotas-chart
              graph-title="Órgãos autuadores"
              :graph-data="issuingAuthorities"
              :loading="objectLoad.graph1"
              @uf-change="changeAuthorityUf"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <cm-frotas-map-chart
              graph-title="Infrações por Estado"
              :graph-data="mapData.list"
              :loading="mapData.loading"
            />
          </div>
        </div>

        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <cm-frotas-cnpj-chart
              graph-title="Multas por empresa"
              :graph-data="cnpjData.list"
              :loading="cnpjData.loading"
            />
          </div>
        </div>
      </div>

      <!-- Vehicle Table -->
      <div class="row">
        <div class="col-md-12 mt-4 mb-2">
          <h5><strong>Multas por veículo</strong></h5>
        </div>

        <!-- Search -->
        <div class="col-md-4">
          <input
            class="input form-control"
            @keypress.enter="find()"
            v-model="generic"
            placeholder="Pesquisar por Placa ou Renavam"
          >
          <search-icon @click="find()" class="icon icon-within"/>
        </div>

        <Vehicles
          :vehicles="vehicles"
          :loading="objectLoad.vehicles"
          :endDate="endDate"
          :startDate="startDate"
          :periodType="periodType"
          :entityStatusId="entityStatusId"
        />

        <div class="col-md-12">
          <Pagination
            v-model="meta.currentPage"
            :totalCount="meta.totalCount"
            :totalPages="meta.totalPages"
            @pagination="pagination"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import GetAccount from '@/mixins/GetAccount'
  import GetAccountApps from '@/mixins/GetAccountApps'

  import Card from '@/components/dashboard/dashboard_cm_frotas/Card.vue'
  import Vehicles from '@/components/dashboard/dashboard_cm_frotas/Vehicles.vue'

  import Pagination from '@/components/shares/specialTag/Pagination.vue';
  import CmFrotasChart from '@/components/charts/CmFrotasChart.vue';
  import CmFrotasPieChart from '@/components/charts/CmFrotasPieChart.vue';
  import CmFrotasMapChart from '@/components/charts/CmFrotasMapChart.vue';
  import CmFrotasCnpjChart from '@/components/charts/CmFrotasCnpjChart.vue';
  import FutureAlert from '@/components/dashboard/cards/FutureAlert.vue';

  import { SearchIcon, InfoIcon, FilterIcon } from 'vue-feather-icons'
  import { IssuingAuthority } from '@/models/IssuingAuthority'
  import { VEHICLE_STATUS, VEHICLE_STATUS_FILTER_TABLE } from '@/utils/vehicleStatus'
  import AlertBannerRecords from '@/components/shares/AlertBannerRecords.vue';

  const path = 'maestro/tickets/aggregation'
  const MAX_COMPANIES = 10
  // multas que ainda podem vencer (RF1)
  const FUTURE_ALERT_STATUS = ['opened', 'autuacao', 'em_recurso']

  const toISODate = date => date.toISOString().split('T')[0]
  const futureDate = days => {
    const date = new Date()
    date.setDate(date.getDate() + days)
    return toISODate(date)
  }
  const buildAuthorities = list => {
    if (list.length <= 10)
      return list

    const cloneList = [...list]
    cloneList.sort((a, b) => (a.sum < b.sum) ? 1 : ((b.sum < a.sum) ? -1 : 0))
    const newList = cloneList.slice(0, 9)
    const rest = cloneList.slice(9)

    newList.push({
      count: rest.map(e => e.count).reduce((a, b) => a + b),
      sum: rest.map(e => e.sum).reduce((a, b) => a + b),
      issuingAuthority: 'Outros',
    })

    return newList
  }

  export default {
    created() {
      this.loadEntityStatus()
      this.initializeData()
    },
    components: {
      AlertBannerRecords, Card, CmFrotasChart, CmFrotasCnpjChart, CmFrotasMapChart, CmFrotasPieChart, FilterIcon,
      FutureAlert, InfoIcon, Pagination, SearchIcon, Vehicles },
    computed: {
      queryDate() {
        return {
          start_date: this.startDate,
          end_date: this.endDate && `${this.endDate} 23:59:59.999`,
          period_type: this.periodType,
          entity_status_id: this.entityStatusId
        }
      },
      queryGeneric() {
        return this.generic ? { generic: this.generic } : {}
      },
      appLink() {
        return this.entityStatusId ? `/cm-frotas?q[entity_status_id]=${this.entityStatusId}` : '/cm-frotas'
      },
    },
    data: () => ({
      objectLoad: {
        status: true,
        graph: true,
        graph1: true,
        vehicles: true,
      },
      startDate: '',
      endDate: '',
      periodType: 'occurred_at',
      entityStatusId: String(VEHICLE_STATUS.ENABLED),
      entityStatuses: [],
      generic: '',
      isLoadingData: false,
      authorityUf: '',
      vehiclesMessage: 'Ainda não existem veículos cadastrados no sistema. <a href="/definitions?tab=vehicles">Clique aqui</a> para ir para o cadastro de veículos.',
      scheduleMessage: 'Ainda não existem multas registradas no sistema. <a href="/cm-frotas/configuracoes">Clique aqui</a> para ir para o agendamento de consultas.',
      meta: {
        perPage: 5,
        currentPage: 1
      },
      classifications: [],
      listStatus: [{}, {}, {}, {}, {}, {}, {}, {}],
      vehicles: [],
      issuingAuthorities: [],
      mapData: { list: [], loading: true },
      cnpjData: { list: [], loading: true },
      futureAlertData: {
        count: 0,
        sum: 0,
        loading: true,
        days: 7,
        options: [
          { value: 7, label: '7 dias' },
          { value: 15, label: '15 dias' },
          { value: 30, label: '30 dias' },
        ]
      },
    }),
    methods: {
      async initializeData() {
        if (this.isLoadingData)
          return

        this.isLoadingData = true

        const methods = [
          this.loadFutureAlert,
          this.loadStatus,
          this.loadClassification,
          this.loadIssuingAuthority,
          this.loadMapData,
          this.loadCnpjData,
          this.loadVehicles,
        ]

        for (const method of methods)
          await method()

        this.isLoadingData = false
      },
      find() {
        this.meta.currentPage = 1
        this.loadVehicles()
      },
      loadEntityStatus() {
        this.entityStatuses = VEHICLE_STATUS_FILTER_TABLE
      },
      loadClassification() {
        const params = {
          query: this.queryDate,
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['ticket_class', 'ticket_subclass'],
          meta: false,
          per_page: 1000
        }

        this.objectLoad.graph = true
        return this.axios.get(path, { params })
          .then(response => this.classifications = this.$parse.toCamelcase(response.data))
          .finally(() => this.objectLoad.graph = false)
      },
      loadIssuingAuthority() {
        const params = {
          query: { ...this.queryDate, uf: this.authorityUf },
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['issuing_authority'],
          meta: false,
          per_page: 1000
        }

        this.objectLoad.graph1 = true
        return this.axios.get(path, { params })
          .then(response => this.$parse.toCamelcase(response.data))
          .then(data => buildAuthorities(data))
          .then(authorities => authorities.map(a => ({
            name: IssuingAuthority.label(a.issuingAuthority) || 'Sem informação',
            y: a.sum,
            count: a.count
          })))
          .then(data => this.issuingAuthorities = data)
          .finally(() => this.objectLoad.graph1 = false)
      },
      loadVehicles() {
        const params = {
          query: { ...this.queryDate, ...this.queryGeneric },
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['plate', 'renavam', 'cnpj'],
          page: this.meta.currentPage,
          per_page: this.meta.perPage
        }

        this.objectLoad.vehicles = true
        return this.axios.get(path, { params })
          .then(response => {
            this.vehicles = this.$parse.toCamelcase(response.data)
            this.meta = this.$parse.toCamelcase(response.meta)
          })
          .finally(() => this.objectLoad.vehicles = false)
      },
      loadStatus() {
        const cards = [
          { status: 'paid', title: 'PAGAS', color: 'green' },
          { status: 'encerrado', title: 'ENCERRADO', color: 'blue' },
          { status: 'autuacao', title: 'AUTUAÇÃO', color: 'orange' },
          { status: 'opened', title: 'EM ABERTO', color: 'yellow' },
          { status: 'expired', title: 'VENCIDAS', color: 'red' },
          { status: 'em_recurso', title: 'EM RECURSO', color: 'purple' },
          { status: 'not_available', title: 'SAIU DO SITE', color: 'brown' },
          { status: 'undefined', title: 'SEM INFORMAÇÃO', color: 'dark-gray' },
        ]

        const params = {
          query: {
            ...this.queryDate,
            status: cards.map(card => card.status)
          },
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['status'],
          per_page: cards.length
        }

        this.objectLoad.status = true
        return this.axios.get(path, { params })
          .then(response => response.data)
          .then(data => {
            this.listStatus = cards.map(card => ({
              ...(data.find(item => item.status === card.status) || {}),
              ...card,
              link: this.buildStatusLink(card.status)
            }))
          })
          .finally(() => this.objectLoad.status = false)
      },
      loadFutureAlert() {
        const params = {
          query: {
            status: FUTURE_ALERT_STATUS,
            period_type: 'boleto_due_date',
            start_date: toISODate(new Date()),
            end_date: futureDate(this.futureAlertData.days)
          },
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['status'],
          meta: false,
          per_page: FUTURE_ALERT_STATUS.length
        }

        this.futureAlertData.loading = true
        return this.axios.get(path, { params })
          .then(response => (response.data || []).reduce(
            (total, item) => ({ count: total.count + (item.count || 0), sum: total.sum + (item.sum || 0) }),
            { count: 0, sum: 0 }
          ))
          .then(total => {
            this.futureAlertData.count = total.count
            this.futureAlertData.sum = total.sum
          })
          .finally(() => this.futureAlertData.loading = false)
      },
      loadMapData() {
        const params = {
          query: this.queryDate,
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['uf'],
          meta: false,
          per_page: 30
        }

        this.mapData.loading = true
        return this.axios.get(path, { params })
          .then(response => this.mapData.list = response.data || [])
          .finally(() => this.mapData.loading = false)
      },
      loadCnpjData() {
        const params = {
          query: this.queryDate,
          aggregation: {
            sum: 'value_cents'
          },
          fields: ['cnpj'],
          meta: false,
          per_page: MAX_COMPANIES + 1
        }

        this.cnpjData.loading = true
        return this.axios.get(path, { params })
          .then(response => (response.data || []).filter(item => item.cnpj).slice(0, MAX_COMPANIES))
          .then(async list => {
            if (list.length === 0)
              return this.cnpjData.list = []

            const companies = await this.findCompanies(list.map(item => item.cnpj))
            this.cnpjData.list = list.map(item => {
              const company = companies.find(c => c.cnpj === item.cnpj)
              return { ...item, name: (company && company.name) || item.cnpj }
            })
          })
          .finally(() => this.cnpjData.loading = false)
      },
      findCompanies(cnpjs) {
        return this.axios.get('companies', { params: { q: { cnpj: cnpjs }, type: ['parent', 'subsidiary'] } })
          .then(response => response.data || [])
          .catch(() => [])
      },
      changeAuthorityUf(uf) {
        this.authorityUf = uf
        this.loadIssuingAuthority()
      },
      buildStatusLink(status) {
        const periodType = encodeURIComponent(this.queryDate.period_type)
        const startDate = encodeURIComponent(this.queryDate.start_date || '')
        const endDate = encodeURIComponent(this.queryDate.end_date || '')
        let link = `/cm-frotas?o[]=updated_at&o[]=desc&q[period_type]=${periodType}&q[start_date]=${startDate}&q[end_date]=${endDate}`
        if (status)
          link += `&q[status]=${encodeURIComponent(status)}`
        if (this.entityStatusId)
          link += `&q[entity_status_id]=${encodeURIComponent(this.entityStatusId)}`

        return link
      },
      pagination(page) {
        this.meta.currentPage = page
        this.loadVehicles()
      },
    },
    watch: {
      'futureAlertData.days'() {
        this.loadFutureAlert()
      },
    },
    mixins: [GetAccount, GetAccountApps],
    name: 'DashboardCMFrotas',
  }
</script>

<style scoped>

  td {
    height: 56px;
  }

  .last-update{
    font-size: 14px;
    color:#051b307a;
  }
   .card{
     font-size: 12px;
   }

   .disabledDashboard {
     -webkit-user-select: none;
     -webkit-touch-callout: none;
     -moz-user-select: none;
     -ms-user-select: none;
     user-select: none;
     filter: blur(8px);
     -webkit-filter: blur(8px);
     z-index: 999;
   }

  .disabledDashboardButton {
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 999;
    padding: 5rem;
    background-color: #fff;
    border: solid #E1E3EB 1.5px;
    border-radius: 5px;
    justify-content: center;
  }

  p, strong {
    margin-bottom: 0.5rem;
  }

</style>
